import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { COMPANY_LOGO } from "@/lib/clients";

const links = [
  { label: "Services", href: "#services" },
  { label: "Clients", href: "#clients" },
  { label: "Legacy", href: "#legacy" },
  { label: "Specialisations", href: "#thermal" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.85, 0, 0.15, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 h-20">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3"
        >
          <img
            src={COMPANY_LOGO}
            alt="Jagdambay Engineering Works"
            className="h-12 w-12 md:h-14 md:w-14 object-contain shrink-0"
          />
          <span className="font-mono text-sm md:text-lg uppercase tracking-widest leading-tight">
            <span className="text-primary">JAGDAMBAY</span>
            <br />
            ENGINEERING WORKS
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-quote-cta"
            onClick={() => scrollTo("#contact")}
            className="hidden sm:inline-flex items-center font-mono text-[11px] uppercase tracking-widest bg-foreground text-background px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
          >
            Get a Quote
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-foreground"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-t border-border"
          >
            <div className="flex flex-col p-6 gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => scrollTo(l.href)}
                  className="font-mono text-left text-sm uppercase tracking-widest text-muted-foreground"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-quote-cta"
                onClick={() => scrollTo("#contact")}
                className="font-mono text-xs uppercase tracking-widest bg-primary text-primary-foreground px-5 py-4 mt-2"
              >
                Get a Quote
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
